// React
import { useContext, useEffect, useState } from "react"

// Components
import {
  BaseTable,
  Card,
  Checkbox,
  CommandButton,
  CustomPrintHover,
  DateInput,
  FooterMenu,
  HeaderMenu,
  Input,
  Label,
  Loading,
  NumberInput,
  PrintTransactionModal,
  Select,
  TableHeader,
  Textarea
} from "components"
import { TableSection } from "./components"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

// Types
import type { DetailType, PurchaseOrderType } from "./PurchaseOrder/types"
import { DataJournalContext, DataJournalContextProvider } from "contexts"

// Utils
import { convertNumber, useApi, useCountryById, useHandleAuth, useLocation, useProvinceById, useRegencyById, useSubdistrictById, useTaxCode, useToggle } from "utils"
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import { useProduct } from "./PurchaseOrder/components/DetailBody/utils"
import { default_option, delivery_option } from "../Register/utils"
import { useSupplier } from "./components/Supplier/utils"
import { useDetail } from "./utils"
import { useEmployeeDetail, useSubdomainSetting, useVendorDetail } from "./PurchaseOrder/utils"
import { IoSendSharp } from "react-icons/io5"

export default function ClosePurchase(): JSX.Element {
  // Hooks
  const { id } = useParams()
  const { data, isLoading } = useDetail(id!)

  if (isLoading || !data) {
    return <Loading loading={isLoading} />
  }

  return (
    <section className="container my-5 flex flex-col gap-2">
      <HeaderMenu title="DATA ENTRY | CLOSE PURCHASE">
        {data.trx_code}
      </HeaderMenu>

      <section className="grid lg:grid-cols-2 gap-x-6 gap-y-2 items-end">
        <Supplier value={data.vendor_id} />

        <div className="flex justify-between items-end">
          <div className="normal-case flex justify-evenly items-center pt-10">
            <div className="flex flex-col gap-2">
              <span>TRADING TERM </span>
              <span>PRICE LEVEL</span>
            </div>

            <div className="px-2 flex flex-col gap-2">
              <IoSendSharp />
              <IoSendSharp />
            </div>

            <div className="flex flex-col gap-2">
              <span>Net {data.payment_term} Days after OEM </span>
              <span>-</span>
            </div>
          </div>

          <div>
            <Checkbox
              className="flex justify-end"
              readOnly value={data.tax_inclusive}>
              TAX-INCLUSIVE
            </Checkbox>
          </div>
        </div>
      </section>

      <Card>
        <Card.Body className="grid lg:grid-cols-2 gap-x-6 gap-y-3">
          <section className="flex flex-col gap-3">
            <section>
              <Deliver value={data.location_id} />

              <Textarea disabled value={data.address} />
            </section>

            <Input disabled label="MEMO" value={data.memo} />
          </section>

          <section className="flex flex-col gap-3">
            <DateInput
              disabled
              label="DATE"
              selected={moment(data.transaction_date).toDate()}
              onChange={() => {}}
            />

            <Input disabled label="SUPPLIER ID" value={data.referensi} />
          </section>
        </Card.Body>
      </Card>

      <div className="overflow-x-auto">
        <BaseTable>
          <TableHeader>
            <tr>
              <th>ITEM CODE</th>
              <th>DESCRIPTION</th>
              <th>ORDER</th>
              <th>RECEIVED</th>
              <th>BACKORDER</th>
              <th>UNIT</th>
              <th>PRICE</th>
              <th>DISC</th>
              <th>TOTAL</th>
              <th>JOB</th>
              <th>TAX</th>
            </tr>
          </TableHeader>

          <tbody>
            <DataJournalContextProvider>
              <BodyTable data={data.podetail} vendor_id={data.vendor_id} />
            </DataJournalContextProvider>
          </tbody>
        </BaseTable>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="w-max-[600px]">
          <TableSection>
            <TableSection.Row
              label="PO STATUS"
              value={
                delivery_option.find(
                  (item) => item.value === String(data.po_delivery_status ?? 1)
                )?.label ?? "-"
              }
            />

            <TableSection.Row
              label="APPROVE STATUS"
              value={default_option[data.approval_status]?.label ?? "-"}
            />

            <TableSection.Row label="APPROVED BY" value={data.approve_by_name} />
          </TableSection>

          <div>
            <Label className="ml-[70px]">
              FREIGHT :
            </Label>

            <NumberInput
              disabled
              inputClass="text-right"
              value={data.freight}
            />
          </div>
        </div>

        <div className="w-[300px] ml-auto">
          <TableSection>
            <TableSection.Row 
              label="SUBTOTAL" 
              value={(convertNumber(data.podetail.reduce((prev, curr) => prev + curr.total, 0)).intoCurrency).toString()}  
              className="text-right"/>
          </TableSection>
        </div>

      </div>

      <FooterMenu
        hasCancelButton
        customElement={{
          print: data.po_delivery_status === 4 ? <Print data={data} /> : undefined,
          save: <Save />
        }}
      />
    </section>
  )
}

function BodyTable(params: {
  data: DetailType[]
  vendor_id: string
}): JSX.Element {
  // Hooks
  const { data, refetch } = useProduct()
  const { project, tax_code } = useContext(DataJournalContext)

  useEffect(() => {
    refetch(params.vendor_id)

    // eslint-disable-next-line
  }, [])

  return (
    <>
      {params.data.map((item, key) => {
        // Vars
        const product = data.find((i) => i.value === item.product_id)
        const project_name: string | undefined = project.data.find(
          (i) => i.value === item.project_id
        )?.label
        const tax_name: string | undefined = tax_code.data.find(
          (i) => i.value === item.tax_id
        )?.label

        return (
          <tr key={key}>
            <td>{product?.product_barcode}</td>
            <td>{product?.product_name}</td>
            <td>{convertNumber(item.qty_order).intoNormalAmount}</td>
            <td>{convertNumber(item.delivered).intoNormalAmount}</td>
            <td>
              {convertNumber(item.qty_order - item.delivered).intoNormalAmount}
            </td>
            <td>{product?.mr_unit_name}</td>
            <td className="text-right">
              {convertNumber(item.price_measure).intoCurrency}
            </td>
            <td className="text-right">
              {convertNumber(item.discount).intoNormalAmount}%
            </td>
            <td className="text-right">
              {convertNumber(item.total).intoCurrency}
            </td>
            <td>{project_name}</td>
            <td>{tax_name}</td>
          </tr>
        )
      })}
    </>
  )
}

function Deliver(params: { value: string }): JSX.Element {
  // Hooks
  const { data, isLoading } = useLocation()

  return (
    <Select
      isDisabled
      label="DELIVER TO"
      placeholder="Select Deliver"
      isLoading={isLoading}
      options={data}
      value={data.find((item) => item.value === params.value) ?? null}
    />
  )
}

function Print(params: {
  data: PurchaseOrderType
}): JSX.Element {
  // Form
  const defaultValues = params.data

  // Hooks
  const { isActive, toggle } = useToggle(false)
  const [withImagePrint, setWithImagePrint] = useState<boolean>(false)

  // Vars
  const vendor_id = defaultValues.vendor_id
  const product = useProduct()
  const setting = useSubdomainSetting()
  const taxCode = useTaxCode()
  const vendor = useVendorDetail(vendor_id)
  const approvedBy = useEmployeeDetail(defaultValues.approve_by)
  const createdBy = useEmployeeDetail(defaultValues.created_by)
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const subdistrict = useSubdistrictById()
  const supplier = useSupplier()
  const { userTokenData } = useHandleAuth()

  // Vars
  const [address, setAddress] = useState<string>("")
  const freight: number = defaultValues?.freight ?? 0
  const detail = defaultValues?.podetail ?? []
  const subtotal: number = detail.reduce((acc, item): number => {
    return acc + (item?.total ?? 0)
  }, 0)
  const tax: number = detail.reduce((acc, item): number => {
    // Vars
    const tax_item = taxCode.data.find((i) => i.value === item?.tax_id)

    return acc + ((item?.total ?? 0) * (tax_item?.rate ?? 0)) / 100
  }, 0)
  const total = subtotal + freight + tax
  const userToken = userTokenData()
  const isLoading: boolean = Boolean(
    approvedBy.isLoading ||
    company.isLoading ||
    country.isLoading ||
    product.isLoading ||
    province.isLoading ||
    regency.isLoading ||
    setting.isLoading ||
    subdistrict.isLoading ||
    supplier.isLoading ||
    taxCode.isReloading ||
    vendor.isLoading
  )

  useEffect(() => {
    if (vendor_id) {
      product.refetch(vendor_id)
    }

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  // Functions
  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }

  // Functions
  const getVendorAddress = (): string => {
    const data = supplier.data.find(item => item.value === vendor_id)

    let generateAddress = data?.address ?? ""

    if (data?.subdistrict_name) {
      generateAddress += ", " + data.subdistrict_name
    }

    if (data?.regency_name) {
      generateAddress += ", " + data.regency_name
    }

    if (data?.province_name) {
      generateAddress += ", " + data.province_name
    }

    if (data?.country_name) {
      generateAddress += ", " + data.country_name
    }

    return generateAddress
  }

  return (
    <>
      <CustomPrintHover
        onClick={(withImage) => {
          setWithImagePrint(withImage)
          toggle()
        }}
      />

      {isActive && (
        <PrintTransactionModal
          isLoading={isLoading}
          toggle={toggle}
        >
          <section className="flex flex-col gap-3">
            <div className="text-center text-2xl font-bold uppercase">
              {userToken?.company_name}
            </div>
    
            <section className="grid grid-cols-2 items-stretch gap-1">
              <section className="font-bold">
                <table className="table-top">
                  <tbody className="font-bold">
                    <tr>
                      <td className="pr-2">ADDRESS</td>
                      <td>: {getAddress()}</td>
                    </tr>
    
                    <tr>
                      <td>PHONE</td>
                      <td>
                        {" "}
                        : {setting.data?.phone ? setting.data?.phone : "-"}
                      </td>
                    </tr>
    
                    <tr>
                      <td>EMAIL</td>
                      <td>
                        {" "}
                        : {setting.data?.email ? setting.data?.email : "-"}
                      </td>
                    </tr>
    
                    <tr>
                      <td>WEBSITE</td>
                      <td>
                        : {setting.data?.website ? setting.data?.website : "-"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>
    
              <section className="flex flex-col justify-between items-end">
                <div className="text-2xl font-bold text-right">
                  CLOSE PURCHASE
                </div>
    
                <table>
                  <tbody className="font-bold ">
                    <tr>
                      <td width={10} className="text-right">
                        DATE :{" "}
                      </td>
                      <td className="whitespace-nowrap text-right">
                        {defaultValues?.transaction_date
                          ? moment(defaultValues?.transaction_date).format(
                              "DD/MM/YYYY"
                            )
                          : "-"}
                      </td>
                    </tr>
    
                    <tr>
                      <td width={10} className="whitespace-nowrap">
                        PURCHASE ORDER NO :{" "}
                      </td>
                      <td className="whitespace-nowrap text-right">
                        {defaultValues.trx_code}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </section>
    
            <table className="table table-bordered">
              <tbody>
                <tr className="bg-primary text-white font-bold">
                  <td colSpan={2}>VENDOR INFORMATION</td>
                </tr>
    
                <tr>
                  <td>
                    <div>VENDOR NAME</div>
                    <div className="font-bold">{defaultValues?._vendor_name}</div>
                  </td>
    
                  <td>
                    <div>CREATED BY</div>
                    <div className="font-bold">
                      {createdBy.data?.employee_name}
                    </div>
                  </td>
                </tr>
    
                <tr>
                  <td colSpan={2}>
                    <div>ADDRESS</div>
                    <div className="font-bold">{getVendorAddress()}</div>
                  </td>
                </tr>
    
                <tr>
                  <td>
                    <div>CONTACT NO</div>
                    <div className="font-bold">{vendor.data?.handphone}</div>
                  </td>
    
                  <td>
                    <div>EMAIL ADDRESS</div>
                    <div className="font-bold">{vendor.data?.email}</div>
                  </td>
                </tr>
              </tbody>
            </table>
    
            <table className="table table-bordered">
              <thead>
                <tr className="bg-primary text-white">
                  <th>#</th>
                  {withImagePrint ? <th>PICTURE</th> : null}
                  <th>DESCRIPTION</th>
                  <th>ORDER</th>
                  <th>BACKORDER</th>
                  <th>RECEIVED</th>
                  <th>UNIT</th>
                  <th>PRICE</th>
                  <th>DISC</th>
                  <th>TOTAL</th>
                  <th>TAX</th>
                </tr>
              </thead>
    
              <tbody>
                {defaultValues?.podetail?.map((item, key) => {
                  // Vars
                  const productData = product.data.find(
                    (i) => i.value === item?.product_id
                  )
                  const taxCodeData = taxCode.data.find(
                    (i) => i.value === item?.tax_id
                  )
                  const imagePath = `${process.env.REACT_APP_BASE_URL_API}/getimage/?imgpath=${productData?.product_foto}`
    
                  return (
                    <tr>
                      <td className="text-center">{key + 1}</td>
                      {withImagePrint ? (
                        <td width={150}>
                          <img
                            width={150}
                            height={150}
                            src={imagePath}
                            alt={productData?.product_name}
                          />
                        </td>
                      ) : null}
                      <td>
                        <div>{productData?.product_barcode}</div>
                        <div className="mb-4">{productData?.product_name}</div>
                        <div>COLOUR : {productData?.mr_product_colour_name}</div>
                        <div>STYLE : {productData?.mr_product_style_name}</div>
                        <div>WEIGHT : {productData?.weight}</div>
                        <div>UNIT : {productData?.mr_unit_name}</div>
                      </td>
                      <td className="text-right">
                        {convertNumber(item?.qty_order ?? 0).intoCurrency}
                      </td>
                      <td className="text-right">
                        {convertNumber(item?.delivered ?? 0).intoCurrency}
                      </td>
                      <td className="text-right">
                        {
                          convertNumber(
                            (item?.qty_order ?? 0) - (item?.delivered ?? 0)
                          ).intoCurrency
                        }
                      </td>
                      <td>{productData?.mr_unit_name}</td>
                      <td className="text-right">
                        {convertNumber(item?.price_measure ?? 0).intoCurrency}
                      </td>
                      <td className="text-right">
                        {convertNumber(item?.discount ?? 0).intoCurrency}%
                      </td>
                      <td className="text-right">
                        {convertNumber(item?.total ?? 0).intoCurrency}
                      </td>
                      <td>{taxCodeData?.label}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
    
            <section className="grid grid-cols-5 items-stretch gap-5">
              <section className="col-span-2">
                <table className="table table-bordered">
                  <tbody>
                    <tr className="bg-primary text-white font-bold">
                      <td>Additional Notes:</td>
                    </tr>
    
                    <tr className="min-h-[300px]">
                      <td>{defaultValues?.memo}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
              <div />
    
              <section className="col-span-2">
                <table className="w-full">
                  <tbody>
                    <tr className="font-bold">
                      <td>SUBTOTAL</td>
                      <td className="text-right border-b w-[100px]">
                        {convertNumber(subtotal).intoCurrency}
                      </td>
                    </tr>
    
                    <tr>
                      <td>FREIGHT</td>
                      <td className="text-right border-b">
                        {convertNumber(freight).intoCurrency}
                      </td>
                    </tr>
    
                    <tr>
                      <td>TAX</td>
                      <td className="text-right border-b">
                        {convertNumber(tax).intoCurrency}
                      </td>
                    </tr>
    
                    <tr>
                      <td>TOTAL AMOUNT</td>
                      <td className="text-right border-b">
                        {convertNumber(total).intoCurrency}
                      </td>
                    </tr>
    
                    <tr>
                      <td>PAYMENT</td>
                      <td className="text-right border-b">
                        {convertNumber(0).intoCurrency}
                      </td>
                    </tr>
    
                    <tr className="font-bold">
                      <td>BALANCE</td>
                      <td className="text-right border-b border-double">
                        {convertNumber(total).intoCurrency}
                      </td>
                    </tr>
    
                    <tr className="bg-primary text-white font-bold">
                      <td colSpan={3}>Authorized By</td>
                    </tr>
    
                    <tr className="h-[100px]" />
    
                    <tr>
                      <td colSpan={3}>{approvedBy.data?.employee_name}</td>
                    </tr>
    
                    <tr>
                      <td colSpan={3}>{approvedBy.data?.job_position_name}</td>
                    </tr>
                  </tbody>
                </table>
              </section>
            </section>
          </section>
        </PrintTransactionModal>
      )}
    </>
  )
}

function Save(): JSX.Element {
  // Hooks
  const api = useApi()
  const navigate = useNavigate()
  const { id } = useParams()
  const { isActive, setActive } = useToggle(false)

  function onSubmit() {
    setActive(true)

    toast
      .promise(
        api.post(
          `/purchaseorder/manualclose?purchase_order_id=${id}&date_close=${moment().format(
            "YYYY-MM-DD"
          )}`
        ),
        {
          loading: "Closing Purchase Order...",
          success: (res) => res.data.message,
          error: (err) => err.response.data?.message ?? err.response.message
        }
      )
      .then(() => {
        navigate(-1)
      })
      .catch(() => {})
      .finally(() => {
        setActive(false)
      })
  }

  return (
    <CommandButton
      actiontype="save"
      label="CLOSE PURCHASE"
      loading={isActive ? "true" : undefined}
      onClick={onSubmit}
    />
  )
}

function Supplier(params: { value: string }): JSX.Element {
  // Hooks
  const { data, isLoading } = useSupplier()

  return (
    <Select
      isDisabled
      label="SUPPLIER"
      className="grow"
      placeholder="Select Supplier"
      isLoading={isLoading}
      value={data.find((item) => item.value === params.value) ?? null}
      options={data}
    />
  )
}
